import React, { useEffect, useRef, useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Avatar,
  Box,
  Button,
  FormControl,
  FormLabel,
  HStack,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Skeleton,
  Text,
  Textarea,
  VStack,
  useToast,
} from "@chakra-ui/react";
import {
  IoCameraOutline,
  IoCloseOutline,
  IoGlobeOutline,
  IoMailOutline,
  IoPencilOutline,
  IoPersonOutline,
  IoTimeOutline,
} from "react-icons/io5";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { reload, updateProfile, verifyBeforeUpdateEmail } from "firebase/auth";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { httpsCallable } from "firebase/functions";
import { auth, db, functions } from "../../firebase/firebase-config";
import { FaInstagram, FaLinkedinIn } from "react-icons/fa";
import { FaXTwitter } from "react-icons/fa6";
import { InterestPicker } from "../Structure/InterestPicker";
import { emptySocials, normalizeSocials, SOCIALS, socialsFromUser } from "../../lib/profileSocials";

const SOCIAL_ICONS = {
  instagram: FaInstagram,
  x: FaXTwitter,
  linkedin: FaLinkedinIn,
  website: IoGlobeOutline,
};

// The profile function isn't deployed yet, or the local functions emulator
// isn't running. Either way the direct write is the same shape it saves.
const profileFunctionMissing = (error) => {
  const code = error?.code || "";
  if (code === "functions/not-found" || code === "functions/unavailable") return true;
  if (code === "functions/internal") {
    return /connect|fetch|network|unavailable|cors/i.test(error?.message || "");
  }
  return false;
};

// Simple edit-profile modal: avatar, display name, bio, interests.
// The save goes through saveUserProfile, which checks the photo and the
// interest ids before writing them onto the signed-in user.
const FOCUS_COPY = {
  photo: {
    title: "Add a photo",
    subtitle: "A face helps people recognize you at events.",
  },
  bio: {
    title: "Write a bio",
    subtitle: "A short line so people know who you are.",
  },
  interests: {
    title: "Pick your interests",
    subtitle: "Choose a few so we can match you with the right events.",
  },
};

export const EditProfileModal = ({ isOpen, onClose, user, onSaved, focusSection = null }) => {
  const toast = useToast();
  const fileInputRef = useRef(null);
  const photoSectionRef = useRef(null);
  const bioSectionRef = useRef(null);
  const bioInputRef = useRef(null);
  const interestsSectionRef = useRef(null);
  const [pulse, setPulse] = useState(null);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [displayName, setDisplayName] = useState("");
  const [bio, setBio] = useState("");
  const [socials, setSocials] = useState(emptySocials);
  const [avatarUrl, setAvatarUrl] = useState("");
  const [selectedInterestIds, setSelectedInterestIds] = useState([]); // string ids of interest refs

  // Email change flow state. We never mutate email in the main "Save Changes"
  // payload — it goes through Firebase Auth's verify-before-update flow, then
  // a sync step mirrors the confirmed address into Firestore.
  const [currentEmail, setCurrentEmail] = useState("");
  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [sendingEmailVerification, setSendingEmailVerification] = useState(false);
  const [pendingEmail, setPendingEmail] = useState(null);

  // ── Load user + interest catalog on open ───────────────────────────────
  useEffect(() => {
    if (!isOpen || !user) return;
    let cancelled = false;

    (async () => {
      setLoading(true);
      try {
        // Refresh the auth user first so `auth.currentUser.email` reflects
        // any email change the user just confirmed via the verification link.
        try {
          if (auth.currentUser) await reload(auth.currentUser);
        } catch (_) {
          /* non-fatal: we just fall back to the cached email */
        }

        const userSnap = await getDoc(doc(db, "users", user.uid));
        if (cancelled) return;

        const data = userSnap.exists() ? userSnap.data() : {};
        setDisplayName(data.displayName || user.displayName || "");
        setBio(data.bio || "");
        setSocials(socialsFromUser(data));
        setAvatarUrl(data.avatarUrl || user.photoURL || "");
        setSelectedInterestIds(
          (data.interests || [])
            .map((r) => r?.id || r?.path?.split("/").pop() || null)
            .filter(Boolean)
        );

        // Use the freshly-reloaded auth email as the source of truth, and
        // mirror it into Firestore if it has drifted (i.e. the user just
        // confirmed a change since the last time we wrote the doc).
        const authEmail = auth.currentUser?.email || data.email || "";
        setCurrentEmail(authEmail);
        setNewEmail("");
        setIsEditingEmail(false);
        setPendingEmail(null);

        if (authEmail && data.email && authEmail !== data.email) {
          try {
            await setDoc(
              doc(db, "users", user.uid),
              { email: authEmail },
              { merge: true }
            );
          } catch (syncErr) {
            console.warn("Failed to sync updated email to Firestore:", syncErr);
          }
        }
      } catch (e) {
        console.error(e);
        toast({
          title: "Couldn't load profile",
          description: e.message,
          status: "error",
          duration: 4000,
          isClosable: true,
        });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, user, toast]);

  // Opening from a profile task scrolls that section into view and rings it
  // so the next action is obvious without hiding the rest of the form.
  useEffect(() => {
    if (!isOpen || loading || !focusSection) return undefined;
    const section = {
      photo: photoSectionRef,
      bio: bioSectionRef,
      interests: interestsSectionRef,
    }[focusSection]?.current;
    const frame = requestAnimationFrame(() => {
      section?.scrollIntoView({ behavior: "smooth", block: "center" });
      if (focusSection === "bio") bioInputRef.current?.focus();
    });
    setPulse(focusSection);
    const timer = setTimeout(() => setPulse(null), 1800);
    return () => {
      cancelAnimationFrame(frame);
      clearTimeout(timer);
    };
  }, [isOpen, loading, focusSection]);

  // ── Avatar upload ──────────────────────────────────────────────────────
  const handleAvatarUpload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast({
        title: "Invalid file",
        description: "Please select an image.",
        status: "error",
        duration: 3000,
      });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: "File too large",
        description: "Image must be under 5MB.",
        status: "error",
        duration: 3000,
      });
      return;
    }

    setUploading(true);
    try {
      const storage = getStorage();
      const path = `users/${user.uid}/avatar_${Date.now()}_${file.name}`;
      const sref = ref(storage, path);
      await uploadBytes(sref, file);
      const url = await getDownloadURL(sref);
      setAvatarUrl(url);
    } catch (e) {
      toast({
        title: "Upload failed",
        description: e.message,
        status: "error",
        duration: 4000,
      });
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  // ── Email change flow ──────────────────────────────────────────────────
  // Kicks off Firebase Auth's verify-before-update flow: a link is sent to
  // the NEW address, and the email only switches once the user clicks it.
  // Firestore is synced the next time the modal opens (see the load effect).
  const handleSendEmailVerification = async () => {
    if (!auth.currentUser) return;

    const next = newEmail.trim().toLowerCase();
    if (!next || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(next)) {
      toast({
        title: "Invalid email",
        description: "Please enter a valid email address.",
        status: "warning",
        duration: 3000,
      });
      return;
    }
    if (next === (currentEmail || "").toLowerCase()) {
      toast({
        title: "Same email",
        description: "That's already your current address.",
        status: "info",
        duration: 3000,
      });
      return;
    }

    setSendingEmailVerification(true);
    try {
      await verifyBeforeUpdateEmail(auth.currentUser, next);
      setPendingEmail(next);
      setIsEditingEmail(false);
      setNewEmail("");
      toast({
        title: "Verification email sent",
        description: `Click the link sent to ${next} to finish the change. Your current email stays active until you confirm.`,
        status: "success",
        duration: 6000,
        isClosable: true,
      });
    } catch (e) {
      let description = e.message || "Please try again.";
      if (e.code === "auth/requires-recent-login") {
        description =
          "For security, please sign out and sign back in before changing your email.";
      } else if (e.code === "auth/email-already-in-use") {
        description = "That email is already attached to another account.";
      } else if (e.code === "auth/invalid-email") {
        description = "That doesn't look like a valid email.";
      }
      toast({
        title: "Couldn't send verification",
        description,
        status: "error",
        duration: 6000,
        isClosable: true,
      });
    } finally {
      setSendingEmailVerification(false);
    }
  };

  // ── Save ───────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!user) return;
    if (!displayName.trim()) {
      toast({
        title: "Name required",
        description: "Please enter a display name.",
        status: "warning",
        duration: 3000,
      });
      return;
    }

    const normalized = normalizeSocials(socials);
    if (normalized.error) {
      toast({
        title: "Check your socials",
        description: normalized.error,
        status: "warning",
        duration: 3000,
      });
      return;
    }

    setSaving(true);
    try {
      const payload = {
        displayName: displayName.trim(),
        bio: bio.trim(),
        avatarUrl: avatarUrl || null,
        interestIds: selectedInterestIds,
        socials: normalized.socials,
      };

      let savedName = payload.displayName;
      let savedAvatar = payload.avatarUrl;

      try {
        const saveUserProfile = httpsCallable(functions, "saveUserProfile");
        const result = await saveUserProfile(payload);
        savedName = result.data?.displayName || savedName;
        savedAvatar = result.data?.avatarUrl ?? savedAvatar;
      } catch (error) {
        if (!profileFunctionMissing(error)) throw error;
        console.warn("saveUserProfile unavailable, writing the profile directly:", error);
        await setDoc(
          doc(db, "users", user.uid),
          {
            displayName: savedName,
            bio: payload.bio,
            avatarUrl: savedAvatar,
            interests: selectedInterestIds.map((id) => doc(db, "interests", id)),
            socials: payload.socials,
          },
          { merge: true }
        );
      }

      // The navbar reads Auth photo and name immediately. The function also
      // updates Auth, but this copy refreshes the session already open.
      if (auth.currentUser) {
        await updateProfile(auth.currentUser, {
          displayName: savedName,
          photoURL: savedAvatar || null,
        });
      }

      toast({
        title: "Profile updated",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
      if (onSaved) onSaved();
      onClose();
    } catch (e) {
      toast({
        title: "Couldn't save profile",
        description: e.message,
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  const header = FOCUS_COPY[focusSection] || {
    title: "Edit Profile",
    subtitle: "Update what others see about you",
  };
  const sectionProps = (section) => ({
    ref: { photo: photoSectionRef, bio: bioSectionRef, interests: interestsSectionRef }[section],
    borderRadius: "xl",
    px: 2,
    py: 2,
    mx: -2,
    transition: "box-shadow 0.3s ease, background 0.3s ease",
    boxShadow: pulse === section ? "0 0 0 2px #508cf7" : "none",
    bg: pulse === section ? "blue.50" : "transparent",
  });

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size={{ base: "full", md: "md" }}
      isCentered
      scrollBehavior="inside"
      motionPreset="none"
    >
      <ModalOverlay backdropFilter="blur(4px)" bg="blackAlpha.500" />
      <ModalContent borderRadius={{ base: 0, md: "2xl" }} overflow="hidden">
        <ModalHeader
          pb={2}
          pt={5}
          px={6}
          borderBottom="1px solid"
          borderColor="gray.100"
        >
          <HStack spacing={3} align="center">
            <Box
              bg="blue.50"
              p={2}
              borderRadius="lg"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <Icon as={IoPersonOutline} color="#508cf7" boxSize={4} />
            </Box>
            <Box>
              <Text fontSize="lg" fontWeight="700" color="gray.800">
                {header.title}
              </Text>
              <Text fontSize="xs" color="gray.400">
                {header.subtitle}
              </Text>
            </Box>
          </HStack>
          <ModalCloseButton top={4} right={4} />
        </ModalHeader>

        <ModalBody px={6} py={5}>
          {loading ? (
            <VStack spacing={4} align="stretch">
              <Skeleton height="120px" borderRadius="xl" />
              <Skeleton height="32px" borderRadius="lg" />
              <Skeleton height="80px" borderRadius="lg" />
              <Skeleton height="60px" borderRadius="lg" />
            </VStack>
          ) : (
            <VStack spacing={5} align="stretch">
              {/* Avatar */}
              <Box {...sectionProps("photo")}>
              <FormControl>
                <FormLabel
                  fontSize="xs"
                  fontWeight="semibold"
                  color="gray.600"
                  mb={2}
                >
                  Photo
                </FormLabel>
                <HStack spacing={4}>
                  <Box position="relative">
                    <Avatar
                      size="xl"
                      name={displayName || "User"}
                      src={avatarUrl || undefined}
                    />
                    {avatarUrl && (
                      <IconButton
                        aria-label="Remove photo"
                        icon={<IoCloseOutline />}
                        size="xs"
                        position="absolute"
                        top={-1}
                        right={-1}
                        borderRadius="full"
                        bg="white"
                        shadow="md"
                        onClick={() => setAvatarUrl("")}
                      />
                    )}
                  </Box>
                  <VStack align="flex-start" spacing={1}>
                    <Button
                      size="sm"
                      variant="outline"
                      borderRadius="full"
                      fontWeight="600"
                      leftIcon={<Icon as={IoCameraOutline} boxSize={4} />}
                      isLoading={uploading}
                      onClick={() => fileInputRef.current?.click()}
                    >
                      {avatarUrl ? "Change photo" : "Upload photo"}
                    </Button>
                    <Text fontSize="11px" color="gray.400">
                      JPG or PNG, under 5 MB.
                    </Text>
                  </VStack>
                </HStack>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={(e) => handleAvatarUpload(e.target.files?.[0])}
                />
              </FormControl>
              </Box>

              {/* Display name */}
              <FormControl isRequired>
                <FormLabel
                  fontSize="xs"
                  fontWeight="semibold"
                  color="gray.600"
                  mb={2}
                >
                  Name
                </FormLabel>
                <Input
                  size="sm"
                  borderRadius="lg"
                  placeholder="Your name"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  focusBorderColor="#508cf7"
                />
              </FormControl>

              {/* Email — verified change flow */}
              <FormControl>
                <FormLabel
                  fontSize="xs"
                  fontWeight="semibold"
                  color="gray.600"
                  mb={2}
                >
                  Email
                </FormLabel>

                {!isEditingEmail ? (
                  <VStack align="stretch" spacing={2}>
                    <HStack
                      justify="space-between"
                      bg="gray.50"
                      border="1px solid"
                      borderColor="gray.200"
                      borderRadius="lg"
                      px={3}
                      py={2}
                    >
                      <HStack spacing={2} minW={0}>
                        <Icon as={IoMailOutline} color="gray.400" boxSize={4} />
                        <Text
                          fontSize="sm"
                          color="gray.700"
                          noOfLines={1}
                        >
                          {currentEmail || "No email on file"}
                        </Text>
                      </HStack>
                      <Button
                        size="xs"
                        variant="ghost"
                        color="#508cf7"
                        fontWeight="600"
                        leftIcon={<Icon as={IoPencilOutline} boxSize={3} />}
                        onClick={() => {
                          setIsEditingEmail(true);
                          setNewEmail("");
                        }}
                      >
                        Change
                      </Button>
                    </HStack>

                    {pendingEmail && (
                      <HStack
                        spacing={2}
                        bg="orange.50"
                        border="1px solid"
                        borderColor="orange.200"
                        borderRadius="lg"
                        px={3}
                        py={2}
                        align="flex-start"
                      >
                        <Icon
                          as={IoTimeOutline}
                          color="orange.500"
                          boxSize={4}
                          mt="2px"
                        />
                        <Box>
                          <Text fontSize="xs" color="orange.700" fontWeight="600">
                            Pending confirmation
                          </Text>
                          <Text fontSize="11px" color="orange.700">
                            Click the link we sent to{" "}
                            <Text as="span" fontWeight="600">
                              {pendingEmail}
                            </Text>{" "}
                            to finish the change.
                          </Text>
                        </Box>
                      </HStack>
                    )}
                  </VStack>
                ) : (
                  <VStack align="stretch" spacing={2}>
                    <Input
                      size="sm"
                      borderRadius="lg"
                      type="email"
                      placeholder="new@email.com"
                      value={newEmail}
                      onChange={(e) => setNewEmail(e.target.value)}
                      focusBorderColor="#508cf7"
                      autoFocus
                    />
                    <Text fontSize="11px" color="gray.500">
                      We'll send a verification link to the new address. The
                      change only takes effect once you click it.
                    </Text>
                    <HStack justify="flex-end" spacing={2}>
                      <Button
                        size="xs"
                        variant="ghost"
                        onClick={() => {
                          setIsEditingEmail(false);
                          setNewEmail("");
                        }}
                        isDisabled={sendingEmailVerification}
                      >
                        Cancel
                      </Button>
                      <Button
                        size="xs"
                        bg="#508cf7"
                        color="white"
                        fontWeight="600"
                        borderRadius="md"
                        _hover={{ bg: "#226cf1" }}
                        onClick={handleSendEmailVerification}
                        isLoading={sendingEmailVerification}
                      >
                        Send verification
                      </Button>
                    </HStack>
                  </VStack>
                )}
              </FormControl>

              {/* Bio */}
              <Box {...sectionProps("bio")}>
              <FormControl>
                <FormLabel
                  fontSize="xs"
                  fontWeight="semibold"
                  color="gray.600"
                  mb={2}
                >
                  Bio
                </FormLabel>
                <Textarea
                  ref={bioInputRef}
                  size="sm"
                  borderRadius="lg"
                  rows={3}
                  placeholder="A short description of you…"
                  value={bio}
                  onChange={(e) => setBio(e.target.value)}
                  focusBorderColor="#508cf7"
                  resize="vertical"
                />
              </FormControl>
              </Box>

              <FormControl>
                <FormLabel fontSize="xs" fontWeight="semibold" color="gray.600" mb={1}>
                  Socials
                </FormLabel>
                <Text fontSize="11px" color="gray.400" mb={2}>
                  Optional. A username is enough.
                </Text>
                <VStack spacing={2} align="stretch">
                  {SOCIALS.map((field) => (
                    <InputGroup key={field.id} size="sm">
                      <InputLeftElement pointerEvents="none">
                        <Icon as={SOCIAL_ICONS[field.id]} boxSize="14px" color="gray.400" />
                      </InputLeftElement>
                      <Input
                        borderRadius="lg"
                        placeholder={field.placeholder}
                        value={socials[field.id]}
                        onChange={(e) =>
                          setSocials((current) => ({ ...current, [field.id]: e.target.value }))
                        }
                        focusBorderColor="#508cf7"
                      />
                    </InputGroup>
                  ))}
                </VStack>
              </FormControl>

              {/* Interests */}
              <Box {...sectionProps("interests")}>
              <FormControl>
                <FormLabel
                  fontSize="xs"
                  fontWeight="semibold"
                  color="gray.600"
                  mb={2}
                >
                  Interests
                  <Text as="span" fontSize="10px" color="gray.400" ml={2}>
                    {selectedInterestIds.length > 0
                      ? `${selectedInterestIds.length} selected`
                      : "pick a few"}
                  </Text>
                </FormLabel>
                <InterestPicker
                  selectedIds={selectedInterestIds}
                  onChange={(next) => setSelectedInterestIds(next)}
                />
              </FormControl>
              </Box>
            </VStack>
          )}
        </ModalBody>

        <ModalFooter borderTop="1px solid" borderColor="gray.100" py={4} px={6}>
          <Button
            size="sm"
            variant="ghost"
            fontWeight="600"
            onClick={onClose}
            mr={2}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            bg="#508cf7"
            color="white"
            fontWeight="600"
            borderRadius="lg"
            px={5}
            _hover={{ bg: "#226cf1" }}
            onClick={handleSave}
            isLoading={saving}
            isDisabled={loading}
          >
            Save Changes
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
